import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { FileText, Calendar, User, Package, Settings, Download, ChevronDown, ChevronUp, Ruler, Circle, CircleOff, RefreshCw, Star, Lightbulb, Bug, MessageSquare, Wallet, ShieldOff, TrendingUp, Ticket, ShieldCheck, Copy, Eye, Images, ThumbsUp, Meh, ThumbsDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '@/utils/api';

const API = API_URL;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [measureGrants, setMeasureGrants] = useState([]);
  const [measureStats, setMeasureStats] = useState({ count: 0, live_count: 0, expired_count: 0 });
  const [measureLoading, setMeasureLoading] = useState(false);
  const [measureFilter, setMeasureFilter] = useState('all');
  const [reviews, setReviews] = useState([]);
  const [reviewsStats, setReviewsStats] = useState({ approved_count: 0, pending_count: 0, featured_count: 0 });
  const [reviewsFilter, setReviewsFilter] = useState('pending');
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState({ feature_count: 0, bug_count: 0, new_count: 0 });
  const [feedbackType, setFeedbackType] = useState('all');
  const [feedbackStatus, setFeedbackStatus] = useState('all');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  const [unlocks, setUnlocks] = useState([]);
  const [unlockSummary, setUnlockSummary] = useState(null);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockFilter, setUnlockFilter] = useState('all');
  const [unlockTypeFilter, setUnlockTypeFilter] = useState('all');
  const filteredUnlocks = useMemo(() => unlocks.filter(u => {
    if (unlockTypeFilter !== 'all' && u.unlock_type !== unlockTypeFilter) return false;
    if (unlockFilter === 'active') return !u.revoked && !u.reuse_flagged;
    if (unlockFilter === 'reuse_flagged') return u.reuse_flagged && !u.revoked;
    if (unlockFilter === 'revoked') return u.revoked;
    return true;
  }), [unlocks, unlockFilter, unlockTypeFilter]);

  const [invites, setInvites] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [createInviteOpen, setCreateInviteOpen] = useState(false);
  const [newInviteResult, setNewInviteResult] = useState(null);
  const [inviteForm, setInviteForm] = useState({ label: '', email: '', access_type: 'single_project', max_uses: '5', expiry: '', notes: '' });
  const [viewUnlockedFor, setViewUnlockedFor] = useState(null);

  const [exportFeedback, setExportFeedback] = useState([]);
  const [exportFeedbackTotals, setExportFeedbackTotals] = useState({ yes: 0, mostly: 0, not_quite: 0, total: 0, with_comment: 0 });
  const [exportFeedbackLoading, setExportFeedbackLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) fetchExportFeedback();
  }, [isAuthenticated]);

  const fetchExportFeedback = async () => {
    setExportFeedbackLoading(true);
    try {
      const res = await axios.get(`${API}/admin/export-feedback`, { headers: { 'x-admin-password': password } });
      setExportFeedback(res.data.items || []);
      setExportFeedbackTotals(res.data.totals || { yes: 0, mostly: 0, not_quite: 0, total: 0, with_comment: 0 });
    } catch (err) {
      toast.error('Failed to load export feedback');
    } finally {
      setExportFeedbackLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
      fetchMeasureGrants();
      fetchReviews();
      fetchFeedback();
      fetchUnlocks();
      fetchInvites();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) fetchReviews();
  }, [reviewsFilter]);

  useEffect(() => {
    if (isAuthenticated) fetchFeedback();
  }, [feedbackType, feedbackStatus]);

  const fetchFeedback = async () => {
    setFeedbackLoading(true);
    try {
      const res = await axios.get(`${API}/admin/product-feedback?type=${feedbackType}&status=${feedbackStatus}`);
      setFeedback(res.data.items || []);
      setFeedbackStats({
        feature_count: res.data.feature_count || 0,
        bug_count: res.data.bug_count || 0,
        new_count: res.data.new_count || 0,
      });
    } catch (err) {
      toast.error('Failed to load product feedback');
    } finally {
      setFeedbackLoading(false);
    }
  };

  const patchFeedback = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/product-feedback/${id}`, { status });
      toast.success('Status updated');
      fetchFeedback();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const deleteFeedback = async (id) => {
    if (!window.confirm('Delete this feedback item permanently?')) return;
    try {
      await axios.delete(`${API}/admin/product-feedback/${id}`);
      toast.success('Deleted');
      fetchFeedback();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const fetchUnlocks = async () => {
    setUnlockLoading(true);
    try {
      const headers = { 'x-admin-password': password };
      const [listRes, summaryRes] = await Promise.all([
        axios.get(`${API}/project-unlock/admin/list`, { headers }),
        axios.get(`${API}/project-unlock/admin/summary`, { headers }),
      ]);
      setUnlocks(listRes.data.unlocks || []);
      setUnlockSummary(summaryRes.data);
    } catch (err) {
      toast.error('Failed to load project unlocks');
    } finally {
      setUnlockLoading(false);
    }
  };

  const revokeUnlock = async (projectId) => {
    if (!window.confirm('Revoke this project\'s unlock? Only do this after a genuine PayPal refund.')) return;
    try {
      await axios.post(`${API}/project-unlock/admin/revoke/${projectId}`, {}, { headers: { 'x-admin-password': password } });
      toast.success('Unlock revoked');
      fetchUnlocks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to revoke');
    }
  };

  const fetchInvites = async () => {
    setInviteLoading(true);
    try {
      const r = await axios.get(`${API}/project-unlock/admin/invites`, { headers: { 'x-admin-password': password } });
      setInvites(r.data.invites || []);
    } catch (err) {
      toast.error('Failed to load complimentary invites');
    } finally {
      setInviteLoading(false);
    }
  };

  const createInvite = async () => {
    if (!inviteForm.label.trim()) { toast.error('A tester label is required'); return; }
    const body = { label: inviteForm.label.trim(), access_type: inviteForm.access_type };
    if (inviteForm.email.trim()) body.email = inviteForm.email.trim();
    if (inviteForm.access_type === 'limited_pass') body.max_uses = Number(inviteForm.max_uses) || 0;
    if (inviteForm.expiry) body.expiry = new Date(`${inviteForm.expiry}T23:59:59Z`).toISOString();
    if (inviteForm.notes.trim()) body.notes = inviteForm.notes.trim();
    try {
      const r = await axios.post(`${API}/project-unlock/admin/invites`, body, { headers: { 'x-admin-password': password } });
      const link = `${window.location.origin}/measurement-tool?invite=${r.data.token}`;
      setNewInviteResult({ ...r.data, link, label: inviteForm.label.trim() });
      setInviteForm({ label: '', email: '', access_type: 'single_project', max_uses: '5', expiry: '', notes: '' });
      fetchInvites();
      fetchUnlocks(); // also refreshes unlockSummary's invites_active/exhausted/expired/revoked counters
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create invite');
    }
  };

  const revokeInvite = async (inviteId) => {
    if (!window.confirm('Revoke this invite/pass? This stops future redemptions — projects it has already unlocked stay unlocked.')) return;
    try {
      await axios.post(`${API}/project-unlock/admin/invites/${inviteId}/revoke`, {}, { headers: { 'x-admin-password': password } });
      toast.success('Invite revoked');
      fetchInvites();
      fetchUnlocks();
    } catch (err) {
      toast.error('Failed to revoke invite');
    }
  };

  const copyToClipboard = (text, message) => {
    navigator.clipboard.writeText(text);
    toast.success(message || 'Copied');
  };

  const accessTypeLabel = (inv) => {
    if (inv.access_type === 'single_project') return 'Single project';
    if (inv.access_type === 'unlimited') return 'Unlimited';
    return `${inv.max_uses}-project pass`;
  };

  const fetchReviews = async () => {
    setReviewsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reviews?status=${reviewsFilter}`);
      setReviews(res.data.reviews || []);
      setReviewsStats({
        approved_count: res.data.approved_count || 0,
        pending_count: res.data.pending_count || 0,
        featured_count: res.data.featured_count || 0,
      });
    } catch (err) {
      toast.error('Failed to load reviews');
    } finally {
      setReviewsLoading(false);
    }
  };

  const patchReview = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/reviews/${id}`, patch);
      toast.success('Review updated');
      fetchReviews();
    } catch (err) {
      toast.error('Failed to update review');
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`);
      toast.success('Review deleted');
      fetchReviews();
    } catch (err) {
      toast.error('Failed to delete review');
    }
  };

  const fetchMeasureGrants = async () => {
    setMeasureLoading(true);
    try {
      const res = await axios.get(`${API}/measure-tool/grants`);
      setMeasureGrants(res.data.grants || []);
      setMeasureStats({
        count: res.data.count || 0,
        live_count: res.data.live_count || 0,
        expired_count: res.data.expired_count || 0,
      });
    } catch (e) {
      console.error(e);
      toast.error('Failed to fetch measure-tool grants');
    } finally {
      setMeasureLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      if (response.data.success) {
        setIsAuthenticated(true);
        toast.success('Login successful');
      }
    } catch (error) {
      toast.error('Invalid credentials');
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/orders/${orderId}/status`, { status: newStatus });
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update status');
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (filterStatus !== 'all' && order.status !== filterStatus) return false;
    if (filterFrom || filterTo) {
      const raw = order.created_at || order.createdAt;
      if (!raw) return false;
      const t = new Date(raw).getTime();
      if (filterFrom && t < new Date(filterFrom + 'T00:00:00').getTime()) return false;
      if (filterTo && t > new Date(filterTo + 'T23:59:59').getTime()) return false;
    }
    return true;
  });

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-testid="admin-login-title">Admin Login</h1>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="mt-1"
                  data-testid="admin-username-input"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1"
                  data-testid="admin-password-input"
                />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="admin-login-btn">
                Login
              </Button>
            </form>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="dashboard-title">Admin Dashboard</h1>
            <p className="text-gray-600 mt-1">Manage your orders and track status</p>
          </div>
          <div className="flex gap-3">
            <Link to="/admin/analytics">
              <Button variant="outline" data-testid="analytics-btn">
                <FileText className="h-4 w-4 mr-2" /> Analytics
              </Button>
            </Link>
            <Link to="/admin/seo">
              <Button variant="outline" data-testid="seo-settings-btn">
                <Settings className="h-4 w-4 mr-2" /> SEO Settings
              </Button>
            </Link>
            <Link to="/admin/gallery">
              <Button variant="outline" data-testid="gallery-moderation-btn">
                <Images className="h-4 w-4 mr-2" /> Gallery Moderation
              </Button>
            </Link>
            <Button onClick={() => setIsAuthenticated(false)} variant="outline" data-testid="logout-btn">
              Logout
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{orders.length}</p>
              </div>
              <Package className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">New Orders</p>
                <p className="text-2xl font-bold text-gray-900">{orders.filter(o => o.status === 'New').length}</p>
              </div>
              <FileText className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-gray-900">{orders.filter(o => o.status === 'In Progress').length}</p>
              </div>
              <Calendar className="h-8 w-8 text-yellow-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-gray-900">{orders.filter(o => o.status === 'Completed').length}</p>
              </div>
              <User className="h-8 w-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <Label>Status:</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Orders</SelectItem>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Label className="ml-2">From:</Label>
            <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="w-40" data-testid="date-from-filter" />
            <Label>To:</Label>
            <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="w-40" data-testid="date-to-filter" />
            {(filterFrom || filterTo || filterStatus !== 'all') && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterFrom(''); setFilterTo(''); setFilterStatus('all'); }} data-testid="clear-filters">
                Clear
              </Button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <span className="text-sm text-gray-500" data-testid="filter-count">Showing {filteredOrders.length} of {orders.length}</span>
              <Button onClick={fetchOrders} variant="outline" size="sm" data-testid="refresh-btn">
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden" data-testid="orders-table">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading orders...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12 text-gray-600">
              No orders found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rooms</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order) => (
                    <React.Fragment key={order.id}>
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div className="flex items-center gap-2">
                            {expandedOrder === order.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            {order.order_number}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{order.customer_name}</div>
                          <div className="text-sm text-gray-500">{order.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {order.property_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {order.room_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          £{order.total_price.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={order.status}
                            onValueChange={(value) => updateOrderStatus(order.id, value)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="New">New</SelectItem>
                              <SelectItem value="In Progress">In Progress</SelectItem>
                              <SelectItem value="Completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(order.created_at).toLocaleDateString('en-GB')}
                        </td>
                      </tr>
                      {expandedOrder === order.id && (
                        <tr className="bg-gray-50">
                          <td colSpan="7" className="px-6 py-4">
                            <div className="grid grid-cols-2 gap-6">
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-2">Order Details</h4>
                                <div className="text-sm space-y-1">
                                  <p><span className="text-gray-500">Phone:</span> {order.phone || 'Not provided'}</p>
                                  <p><span className="text-gray-500">Property Address:</span> {order.property_address}</p>
                                  <p><span className="text-gray-500">Notes:</span> {order.notes || 'None'}</p>
                                  <p><span className="text-gray-500">Options:</span> {[
                                    order.add_colour && 'Colour',
                                    order.add_3d && '3D',
                                    order.add_furniture && 'Furniture',
                                    order.add_symbols && 'Symbols',
                                    order.elevations_count > 0 && `Elevations (${order.elevations_count})`
                                  ].filter(Boolean).join(', ') || 'None'}</p>
                                </div>
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-2">Uploaded Files</h4>
                                {order.uploaded_files && order.uploaded_files.length > 0 ? (
                                  <div className="space-y-2">
                                    {order.uploaded_files.map((filename) => (
                                      <a
                                        key={`${order.id}-${filename}`}
                                        href={`${API}/uploads/${filename}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Download className="h-4 w-4" />
                                        {filename}
                                      </a>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500">No files uploaded</p>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Measure Tool Access */}
        <div className="mt-12" data-testid="measure-tool-section">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Ruler className="h-5 w-5 text-blue-600" />
                Measure Tool Access
              </h2>
              <p className="text-sm text-gray-600 mt-1">£2 / 24h paywall grants — includes owner whitelist entries.</p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={measureFilter} onValueChange={setMeasureFilter}>
                <SelectTrigger className="w-40" data-testid="measure-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ({measureStats.count})</SelectItem>
                  <SelectItem value="live">Live ({measureStats.live_count})</SelectItem>
                  <SelectItem value="expired">Expired ({measureStats.expired_count})</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={fetchMeasureGrants} variant="outline" size="sm" data-testid="refresh-measure-btn">
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white rounded-lg shadow p-5 border border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Grants</p>
                <p className="text-2xl font-bold text-gray-900" data-testid="measure-stat-total">{measureStats.count}</p>
              </div>
              <Ruler className="h-8 w-8 text-blue-600" />
            </div>
            <div className="bg-white rounded-lg shadow p-5 border border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Live Access</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="measure-stat-live">{measureStats.live_count}</p>
              </div>
              <Circle className="h-8 w-8 text-emerald-500 fill-emerald-500" />
            </div>
            <div className="bg-white rounded-lg shadow p-5 border border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Expired</p>
                <p className="text-2xl font-bold text-gray-500" data-testid="measure-stat-expired">{measureStats.expired_count}</p>
              </div>
              <CircleOff className="h-8 w-8 text-gray-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden" data-testid="measure-table">
            {measureLoading ? (
              <div className="text-center py-10 text-gray-600">Loading grants...</div>
            ) : (measureGrants.filter(g => measureFilter === 'all' ? true : g.status === measureFilter).length === 0) ? (
              <div className="text-center py-10 text-gray-500">No grants match this filter.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="px-4 py-3 text-left">Email</th>
                      <th className="px-4 py-3 text-left">Type</th>
                      <th className="px-4 py-3 text-left">Amount</th>
                      <th className="px-4 py-3 text-left">Granted</th>
                      <th className="px-4 py-3 text-left">Expires</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Reused</th>
                      <th className="px-4 py-3 text-left">PayPal Ref</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {measureGrants
                      .filter(g => measureFilter === 'all' ? true : g.status === measureFilter)
                      .map((g) => (
                        <tr key={g.id} className="hover:bg-gray-50" data-testid="measure-row">
                          <td className="px-4 py-3 font-medium text-gray-900">{g.email}</td>
                          <td className="px-4 py-3">
                            {g.payment_provider === 'owner' ? (
                              <span className="inline-block rounded bg-purple-100 text-purple-800 text-xs px-2 py-0.5 font-medium">Owner</span>
                            ) : (
                              <span className="inline-block rounded bg-blue-100 text-blue-800 text-xs px-2 py-0.5 font-medium">Paid</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {g.amount ? `£${Number(g.amount).toFixed(2)}` : '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {g.granted_at ? new Date(g.granted_at).toLocaleString('en-GB') : '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {g.expires_at ? new Date(g.expires_at).toLocaleString('en-GB') : '—'}
                          </td>
                          <td className="px-4 py-3">
                            {g.status === 'live' ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-xs px-2 py-0.5 font-medium">
                                <Circle className="h-2 w-2 fill-emerald-500 text-emerald-500" />
                                Live
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-600 text-xs px-2 py-0.5 font-medium">
                                <CircleOff className="h-3 w-3" />
                                Expired
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-700 font-mono">{g.verify_count ?? 0}×</td>
                          <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                            {g.payment_id === 'OWNER_ACCESS' ? '—' : g.payment_id}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Reviews Moderation */}
        <div className="mt-8" data-testid="reviews-section">
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Star className="h-5 w-5 text-amber-500" /> Reviews &amp; Testimonials
              </h2>
              <div className="ml-2 flex items-center gap-2 text-xs">
                <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5" data-testid="reviews-pending-count">{reviewsStats.pending_count} pending</span>
                <span className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5" data-testid="reviews-approved-count">{reviewsStats.approved_count} approved</span>
                <span className="rounded-full bg-blue-100 text-blue-800 px-2 py-0.5" data-testid="reviews-featured-count">{reviewsStats.featured_count} featured</span>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <Label className="text-xs">Filter:</Label>
                <Select value={reviewsFilter} onValueChange={setReviewsFilter}>
                  <SelectTrigger className="w-40" data-testid="reviews-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending moderation</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="featured">Featured</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={fetchReviews} data-testid="reviews-refresh">
                  <RefreshCw className="h-3 w-3" />
                </Button>
              </div>
            </div>
            {reviewsLoading ? (
              <div className="text-center py-8 text-gray-500">Loading…</div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-8 text-gray-500" data-testid="reviews-empty">No reviews match this filter.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {reviews.map((r) => (
                  <div key={r.id} className="p-4 flex flex-col md:flex-row gap-3" data-testid={`review-row-${r.id}`}>
                    <div className="md:w-32 shrink-0">
                      <div className="flex items-center gap-0.5 text-amber-500">
                        {Array.from({ length: 5 }).map((_, i) => (
                          // Stars in a fixed-length 5-item array — the index IS the identity here.
                          <Star key={`${r.id}-star-${i}`} className={`h-4 w-4 ${i < r.rating ? 'fill-amber-400 text-amber-500' : 'text-gray-300'}`} />
                        ))}
                      </div>
                      <div className="mt-1 text-[11px] text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : '—'}</div>
                      <div className="mt-0.5 text-[11px] text-gray-500">{r.source || 'plan-measure'}</div>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-gray-900 whitespace-pre-wrap" data-testid={`review-quote-${r.id}`}>
                        {r.quote || <span className="italic text-gray-400">(no quote)</span>}
                      </div>
                      <div className="mt-1 text-xs text-gray-600">
                        <span className="font-medium">{r.name || 'Anonymous'}</span>
                        {r.email && <> · <a className="text-blue-600 hover:underline" href={`mailto:${r.email}`}>{r.email}</a></>}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-start gap-2 md:w-64 justify-end">
                      {!r.approved && (
                        <Button size="sm" onClick={() => patchReview(r.id, { approved: true })} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`review-approve-${r.id}`}>
                          Approve
                        </Button>
                      )}
                      {r.approved && (
                        <Button size="sm" variant="outline" onClick={() => patchReview(r.id, { approved: false })} data-testid={`review-unapprove-${r.id}`}>
                          Unapprove
                        </Button>
                      )}
                      {r.approved && !r.featured && (
                        <Button size="sm" onClick={() => patchReview(r.id, { featured: true })} className="bg-blue-600 hover:bg-blue-700" data-testid={`review-feature-${r.id}`}>
                          Feature
                        </Button>
                      )}
                      {r.featured && (
                        <Button size="sm" variant="outline" onClick={() => patchReview(r.id, { featured: false })} data-testid={`review-unfeature-${r.id}`}>
                          Unfeature
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteReview(r.id)} className="text-red-600 hover:bg-red-50" data-testid={`review-delete-${r.id}`}>
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Project Unlocks (£2.99 per-project monetisation) */}
        <div className="mt-12" data-testid="project-unlock-section">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Wallet className="h-5 w-5 text-emerald-600" />
                Project Unlocks
              </h2>
              <p className="text-sm text-gray-600 mt-1">£2.99 one-off per-project unlock — purchase visibility and revenue.</p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={unlockTypeFilter} onValueChange={setUnlockTypeFilter}>
                <SelectTrigger className="w-36" data-testid="unlock-type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Paid + Comp.</SelectItem>
                  <SelectItem value="single_project">Paid only</SelectItem>
                  <SelectItem value="complimentary_project">Complimentary only</SelectItem>
                </SelectContent>
              </Select>
              <Select value={unlockFilter} onValueChange={setUnlockFilter}>
                <SelectTrigger className="w-44" data-testid="unlock-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ({unlockSummary?.total_unlocks ?? 0})</SelectItem>
                  <SelectItem value="active">Active ({unlockSummary?.active_unlocks ?? 0})</SelectItem>
                  <SelectItem value="reuse_flagged">Reuse flagged ({unlockSummary?.reuse_flagged_count ?? 0})</SelectItem>
                  <SelectItem value="revoked">Revoked ({unlockSummary?.revoked_count ?? 0})</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={fetchUnlocks} variant="outline" size="sm" data-testid="refresh-unlocks-btn">
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
            </div>
          </div>

          <div className="grid md:grid-cols-5 gap-4 mb-4">
            <div className="bg-white rounded-lg shadow p-5 border border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="unlock-stat-revenue">£{(unlockSummary?.total_revenue_gbp ?? 0).toFixed(2)}</p>
                <p className="text-xs text-gray-400">Paid unlocks only</p>
              </div>
              <TrendingUp className="h-8 w-8 text-emerald-500" />
            </div>
            <div className="bg-white rounded-lg shadow p-5 border border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Paid Unlocks</p>
                <p className="text-2xl font-bold text-gray-900" data-testid="unlock-stat-total">{unlockSummary?.total_unlocks ?? 0}</p>
              </div>
              <Wallet className="h-8 w-8 text-blue-600" />
            </div>
            <div className="bg-white rounded-lg shadow p-5 border border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Complimentary Active</p>
                <p className="text-2xl font-bold text-purple-600" data-testid="unlock-stat-complimentary">{unlockSummary?.complimentary_active_count ?? 0}</p>
                <p className="text-xs text-gray-400">Never counted as revenue</p>
              </div>
              <Ticket className="h-8 w-8 text-purple-500" />
            </div>
            <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
              <p className="text-sm text-gray-600">Last 24h / 7d / 30d</p>
              <p className="text-lg font-bold text-gray-900" data-testid="unlock-stat-windows">
                {unlockSummary?.last_24h?.count ?? 0} / {unlockSummary?.last_7d?.count ?? 0} / {unlockSummary?.last_30d?.count ?? 0}
              </p>
              <p className="text-xs text-gray-500">£{(unlockSummary?.last_24h?.revenue_gbp ?? 0).toFixed(2)} / £{(unlockSummary?.last_7d?.revenue_gbp ?? 0).toFixed(2)} / £{(unlockSummary?.last_30d?.revenue_gbp ?? 0).toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-5 border border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Reuse flagged / Revoked</p>
                <p className="text-2xl font-bold text-amber-600" data-testid="unlock-stat-flagged">{unlockSummary?.reuse_flagged_count ?? 0} / {unlockSummary?.revoked_count ?? 0}</p>
              </div>
              <ShieldOff className="h-8 w-8 text-amber-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden" data-testid="unlocks-table">
            {unlockLoading ? (
              <div className="text-center py-10 text-gray-600">Loading unlocks...</div>
            ) : (filteredUnlocks.length === 0) ? (
              <div className="text-center py-10 text-gray-500">No unlocks match this filter.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="px-4 py-3 text-left">Property / Job</th>
                      <th className="px-4 py-3 text-left">Type</th>
                      <th className="px-4 py-3 text-left">Unlocked</th>
                      <th className="px-4 py-3 text-left">Amount</th>
                      <th className="px-4 py-3 text-left">Reference</th>
                      <th className="px-4 py-3 text-left">Exports</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredUnlocks.map((u) => (
                      <tr key={u.project_id} data-testid={`unlock-row-${u.project_id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{u.original_job_identity || u.job_identity || 'Untitled project'}</div>
                          <div className="text-xs text-gray-400">{u.project_id}</div>
                        </td>
                        <td className="px-4 py-3">
                          {u.unlock_type === 'complimentary_project' ? (
                            <span className="rounded-full bg-purple-100 text-purple-800 px-2 py-0.5 text-xs">Complimentary</span>
                          ) : (
                            <span className="rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-xs">Paid</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{u.unlocked_at ? new Date(u.unlocked_at).toLocaleString() : '—'}</td>
                        <td className="px-4 py-3 text-gray-900">£{Number(u.amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                          {u.unlock_type === 'complimentary_project' ? (u.invite_label || 'invite') : u.paypal_order_id}
                        </td>
                        <td className="px-4 py-3 text-gray-900">{u.export_count ?? 0}</td>
                        <td className="px-4 py-3">
                          {u.revoked ? (
                            <span className="rounded-full bg-gray-100 text-gray-600 px-2 py-0.5 text-xs">Revoked</span>
                          ) : u.reuse_flagged ? (
                            <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs">Reuse flagged</span>
                          ) : (
                            <span className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs">Active</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {!u.revoked && (
                            <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => revokeUnlock(u.project_id)} data-testid={`unlock-revoke-${u.project_id}`}>
                              Revoke
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Complimentary Access (admin/tester free-unlock invites) */}
        <div className="mt-12" data-testid="complimentary-access-section">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Ticket className="h-5 w-5 text-purple-600" />
                Complimentary Access
              </h2>
              <p className="text-sm text-gray-600 mt-1">Invite selected testers to unlock projects for free — never a universal code, every invite is individually tracked and revocable.</p>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={fetchInvites} variant="outline" size="sm" data-testid="refresh-invites-btn">
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
              <Button onClick={() => setCreateInviteOpen(true)} size="sm" className="bg-purple-600 hover:bg-purple-700" data-testid="create-invite-btn">
                <Ticket className="h-4 w-4 mr-2" /> Create Invite
              </Button>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-4 mb-4">
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <p className="text-sm text-gray-600">Active invites</p>
              <p className="text-xl font-bold text-emerald-600" data-testid="invite-stat-active">{unlockSummary?.invites_active ?? 0}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <p className="text-sm text-gray-600">Exhausted</p>
              <p className="text-xl font-bold text-gray-700" data-testid="invite-stat-exhausted">{unlockSummary?.invites_exhausted ?? 0}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <p className="text-sm text-gray-600">Expired</p>
              <p className="text-xl font-bold text-gray-500" data-testid="invite-stat-expired">{unlockSummary?.invites_expired ?? 0}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <p className="text-sm text-gray-600">Revoked</p>
              <p className="text-xl font-bold text-red-500" data-testid="invite-stat-revoked">{unlockSummary?.invites_revoked ?? 0}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden" data-testid="invites-table">
            {inviteLoading ? (
              <div className="text-center py-10 text-gray-600">Loading invites...</div>
            ) : invites.length === 0 ? (
              <div className="text-center py-10 text-gray-500">No complimentary invites created yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="px-4 py-3 text-left">Tester / Label</th>
                      <th className="px-4 py-3 text-left">Access Type</th>
                      <th className="px-4 py-3 text-left">Created / Expiry</th>
                      <th className="px-4 py-3 text-left">Used / Remaining</th>
                      <th className="px-4 py-3 text-left">Last Used</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {invites.map((inv) => (
                      <tr key={inv.invite_id} data-testid={`invite-row-${inv.invite_id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{inv.label}</div>
                          {inv.email && <div className="text-xs text-gray-400">{inv.email}</div>}
                          {inv.notes && <div className="text-xs text-gray-400 italic">{inv.notes}</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{accessTypeLabel(inv)}</td>
                        <td className="px-4 py-3 text-gray-600">
                          <div>{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '—'}</div>
                          <div className="text-xs text-gray-400">{inv.expiry ? `Expires ${new Date(inv.expiry).toLocaleDateString()}` : 'No expiry'}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-900">
                          {inv.uses_count ?? 0}{inv.access_type === 'unlimited' ? '' : ` / ${inv.max_uses}`}
                          {inv.access_type !== 'unlimited' && (
                            <div className="text-xs text-gray-400">{inv.remaining} remaining</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{inv.last_used_at ? new Date(inv.last_used_at).toLocaleString() : 'Never'}</td>
                        <td className="px-4 py-3">
                          {inv.status === 'active' && <span className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs">Active</span>}
                          {inv.status === 'exhausted' && <span className="rounded-full bg-gray-100 text-gray-700 px-2 py-0.5 text-xs">Exhausted</span>}
                          {inv.status === 'expired' && <span className="rounded-full bg-gray-100 text-gray-500 px-2 py-0.5 text-xs">Expired</span>}
                          {inv.status === 'revoked' && <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs">Revoked</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => setViewUnlockedFor(inv)} disabled={!(inv.redeemed_project_ids || []).length} data-testid={`invite-view-projects-${inv.invite_id}`} title="View unlocked projects">
                              <Eye className="h-4 w-4" />
                            </Button>
                            {inv.status !== 'revoked' && (
                              <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => revokeInvite(inv.invite_id)} data-testid={`invite-revoke-${inv.invite_id}`}>
                                Revoke
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Create Invite dialog */}
        <Dialog open={createInviteOpen} onOpenChange={(v) => { setCreateInviteOpen(v); if (!v) setNewInviteResult(null); }}>
          <DialogContent className="max-w-md" data-testid="create-invite-dialog">
            {newInviteResult ? (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Invite created</DialogTitle>
                  <DialogDescription>Copy this link now — for security it is shown only this once and cannot be retrieved again (only its hash is stored).</DialogDescription>
                </DialogHeader>
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs font-mono break-all" data-testid="new-invite-link">{newInviteResult.link}</div>
                <div className="flex gap-2">
                  <Button className="flex-1" onClick={() => copyToClipboard(newInviteResult.link, 'Invite link copied')} data-testid="copy-new-invite-link-btn">
                    <Copy className="h-4 w-4 mr-2" /> Copy link
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => copyToClipboard(newInviteResult.token, 'Invite code copied')} data-testid="copy-new-invite-code-btn">
                    Copy code only
                  </Button>
                </div>
                <Button variant="ghost" onClick={() => { setCreateInviteOpen(false); setNewInviteResult(null); }}>Done</Button>
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><Ticket className="h-4 w-4 text-purple-600" /> Create Invite</DialogTitle>
                  <DialogDescription>Generates one cryptographically random, individually-revocable invite. Never a reusable master code.</DialogDescription>
                </DialogHeader>
                <div>
                  <Label htmlFor="invite-label">Tester / label</Label>
                  <Input id="invite-label" placeholder="John Smith - EPC tester" value={inviteForm.label} onChange={(e) => setInviteForm({ ...inviteForm, label: e.target.value })} data-testid="invite-label-input" />
                </div>
                <div>
                  <Label htmlFor="invite-email">Email (optional)</Label>
                  <Input id="invite-email" type="email" placeholder="john@example.com" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} data-testid="invite-email-input" />
                </div>
                <div>
                  <Label>Access type</Label>
                  <Select value={inviteForm.access_type} onValueChange={(v) => setInviteForm({ ...inviteForm, access_type: v })}>
                    <SelectTrigger data-testid="invite-access-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single_project">Single project</SelectItem>
                      <SelectItem value="limited_pass">Tester pass (N projects)</SelectItem>
                      <SelectItem value="unlimited">Unlimited tester</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {inviteForm.access_type === 'limited_pass' && (
                  <div>
                    <Label htmlFor="invite-max-uses">Number of projects</Label>
                    <div className="flex gap-2 items-center">
                      <Input id="invite-max-uses" type="number" min="1" className="w-24" value={inviteForm.max_uses} onChange={(e) => setInviteForm({ ...inviteForm, max_uses: e.target.value })} data-testid="invite-max-uses-input" />
                      {[3, 5, 10].map((n) => (
                        <Button key={n} type="button" size="sm" variant="outline" onClick={() => setInviteForm({ ...inviteForm, max_uses: String(n) })}>{n}</Button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label htmlFor="invite-expiry">Expiry (optional)</Label>
                  <Input id="invite-expiry" type="date" value={inviteForm.expiry} onChange={(e) => setInviteForm({ ...inviteForm, expiry: e.target.value })} data-testid="invite-expiry-input" />
                </div>
                <div>
                  <Label htmlFor="invite-notes">Notes (optional)</Label>
                  <Textarea id="invite-notes" placeholder="Context for your own reference" value={inviteForm.notes} onChange={(e) => setInviteForm({ ...inviteForm, notes: e.target.value })} data-testid="invite-notes-input" />
                </div>
                <Button className="w-full bg-purple-600 hover:bg-purple-700" onClick={createInvite} data-testid="submit-create-invite-btn">Create invite</Button>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* View unlocked projects for an invite */}
        <Dialog open={!!viewUnlockedFor} onOpenChange={(v) => !v && setViewUnlockedFor(null)}>
          <DialogContent className="max-w-md" data-testid="view-unlocked-projects-dialog">
            <DialogHeader>
              <DialogTitle>{viewUnlockedFor?.label} — unlocked projects</DialogTitle>
              <DialogDescription>Projects unlocked using this invite/pass.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {(viewUnlockedFor?.redeemed_project_ids || []).map((pidVal) => {
                const rec = unlocks.find((u) => u.project_id === pidVal);
                return (
                  <div key={pidVal} className="rounded-md border border-gray-200 p-2 text-sm">
                    <div className="font-medium text-gray-900">{rec?.original_job_identity || rec?.job_identity || 'Untitled project'}</div>
                    <div className="text-xs text-gray-400">{pidVal}</div>
                  </div>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>

        {/* Post-Export Feedback (Plan Measure / Floor Plan Creator / Layout) */}
        <div className="mt-8" data-testid="export-feedback-section">
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-emerald-500" /> Export Feedback
              </h2>
              <div className="ml-2 flex items-center gap-2 text-xs">
                <span className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 flex items-center gap-1" data-testid="export-feedback-yes-count"><ThumbsUp className="h-3 w-3" /> {exportFeedbackTotals.yes} yes</span>
                <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 flex items-center gap-1" data-testid="export-feedback-mostly-count"><Meh className="h-3 w-3" /> {exportFeedbackTotals.mostly} mostly</span>
                <span className="rounded-full bg-red-100 text-red-800 px-2 py-0.5 flex items-center gap-1" data-testid="export-feedback-not-quite-count"><ThumbsDown className="h-3 w-3" /> {exportFeedbackTotals.not_quite} not quite</span>
                <span className="rounded-full bg-gray-100 text-gray-700 px-2 py-0.5" data-testid="export-feedback-comment-count">{exportFeedbackTotals.with_comment} with comments</span>
              </div>
              <div className="ml-auto">
                <Button variant="outline" size="sm" onClick={fetchExportFeedback} data-testid="export-feedback-refresh"><RefreshCw className="h-3 w-3" /></Button>
              </div>
            </div>
            {exportFeedbackLoading ? (
              <div className="text-center py-8 text-gray-500">Loading…</div>
            ) : exportFeedback.length === 0 ? (
              <div className="text-center py-8 text-gray-500" data-testid="export-feedback-empty">No export feedback yet.</div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-[480px] overflow-y-auto">
                {exportFeedback.map((f) => (
                  <div key={f.id} className="p-4 flex flex-col md:flex-row gap-3" data-testid={`export-feedback-row-${f.id}`}>
                    <div className="md:w-28 shrink-0">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        f.rating === 'yes' ? 'bg-emerald-100 text-emerald-800' : f.rating === 'mostly' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {f.rating === 'yes' ? 'Yes' : f.rating === 'mostly' ? 'Mostly' : 'Not quite'}
                      </span>
                      <div className="mt-1 text-[11px] text-gray-500">{f.created_at ? new Date(f.created_at).toLocaleString('en-GB') : '—'}</div>
                      <div className="mt-0.5 text-[11px] text-gray-400 uppercase">{f.export_format}</div>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-gray-900 whitespace-pre-wrap">
                        {f.comment || <span className="italic text-gray-400">(no comment)</span>}
                      </div>
                      <div className="mt-1 text-[11px] text-gray-500">
                        {f.floor_count} floor{f.floor_count === 1 ? '' : 's'} · {f.room_count} room{f.room_count === 1 ? '' : 's'} · {f.wall_count} wall{f.wall_count === 1 ? '' : 's'} · {f.opening_count} opening{f.opening_count === 1 ? '' : 's'}
                        {typeof f.time_to_export_seconds === 'number' && <> · {Math.round(f.time_to_export_seconds / 60)} min to export</>}
                      </div>
                    </div>
                    <div className="md:w-40 shrink-0 text-right text-[11px] text-gray-400">
                      <div>{f.browser || '—'} · {f.device_type || '—'}</div>
                      <div>v{f.app_version || '—'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Product Feedback (Help Assistant: feature requests + bug reports) */}
        <div className="mt-8" data-testid="product-feedback-section">
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-500" /> Product Feedback
              </h2>
              <div className="ml-2 flex items-center gap-2 text-xs">
                <span className="rounded-full bg-blue-100 text-blue-800 px-2 py-0.5" data-testid="feedback-feature-count">{feedbackStats.feature_count} feature requests</span>
                <span className="rounded-full bg-red-100 text-red-800 px-2 py-0.5" data-testid="feedback-bug-count">{feedbackStats.bug_count} bug reports</span>
                <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5" data-testid="feedback-new-count">{feedbackStats.new_count} new</span>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <Select value={feedbackType} onValueChange={setFeedbackType}>
                  <SelectTrigger className="w-40" data-testid="feedback-type-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="feature-request">Feature requests</SelectItem>
                    <SelectItem value="bug-report">Bug reports</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={feedbackStatus} onValueChange={setFeedbackStatus}>
                  <SelectTrigger className="w-36" data-testid="feedback-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="under-review">Under review</SelectItem>
                    <SelectItem value="planned">Planned</SelectItem>
                    <SelectItem value="built">Built</SelectItem>
                    <SelectItem value="not-planned">Not planned</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={fetchFeedback} data-testid="feedback-refresh"><RefreshCw className="h-3 w-3" /></Button>
              </div>
            </div>
            {feedbackLoading ? (
              <div className="text-center py-8 text-gray-500">Loading…</div>
            ) : feedback.length === 0 ? (
              <div className="text-center py-8 text-gray-500" data-testid="feedback-empty">No feedback matches this filter.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {feedback.map((f) => (
                  <div key={f.id} className="p-4" data-testid={`feedback-row-${f.id}`}>
                    <div className="flex flex-col md:flex-row gap-3">
                      <div className="md:w-28 shrink-0">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${f.type === 'feature-request' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`}>
                          {f.type === 'feature-request' ? <Lightbulb className="h-3 w-3" /> : <Bug className="h-3 w-3" />}
                          {f.type === 'feature-request' ? 'Feature' : 'Bug'}
                        </span>
                        <div className="mt-1 text-2xl font-bold text-gray-900" data-testid={`feedback-count-${f.id}`}>{f.count}</div>
                        <div className="text-[11px] text-gray-500">request{f.count === 1 ? '' : 's'}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900" data-testid={`feedback-title-${f.id}`}>{f.title}</div>
                        <div className="mt-0.5 text-[11px] text-gray-500">{f.area || f.workspace || '—'}{f.classification ? ` · ${f.classification}` : ''}</div>
                        {(f.examples || []).length > 1 && (
                          <button className="mt-1 text-[11px] text-blue-600 hover:underline flex items-center gap-1" onClick={() => setExpandedFeedback(expandedFeedback === f.id ? null : f.id)} data-testid={`feedback-expand-${f.id}`}>
                            {expandedFeedback === f.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} Similar wording ({f.examples.length})
                          </button>
                        )}
                        {expandedFeedback === f.id && (
                          <ul className="mt-1 ml-4 list-disc text-[11px] text-gray-600 space-y-0.5">
                            {(f.examples || []).map((ex, i) => (
                              <li key={i}>{ex.text}{ex.comment ? ` — ${ex.comment}` : ''}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="flex flex-wrap items-start gap-2 md:w-64 justify-end">
                        <Select value={f.status} onValueChange={(v) => patchFeedback(f.id, v)}>
                          <SelectTrigger className="w-36 h-8 text-xs" data-testid={`feedback-status-${f.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="under-review">Under review</SelectItem>
                            <SelectItem value="planned">Planned</SelectItem>
                            <SelectItem value="built">Built</SelectItem>
                            <SelectItem value="not-planned">Not planned</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="ghost" onClick={() => deleteFeedback(f.id)} className="text-red-600 hover:bg-red-50" data-testid={`feedback-delete-${f.id}`}>Delete</Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
